/* eslint-disable no-unused-vars */
import {
  Box,
  Button,
  Card,
  Flex,
  GridItem,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Icon,
  SimpleGrid,
  Text,
  useDisclosure,
  VStack,
  Alert,
  AlertIcon,
  Badge,
} from '@chakra-ui/react';
import React, { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ViewMerchantData,
  ArchiveMerchant,
} from '../../../reactRedux/thunks/merchnatThunk/merchantThunk';
import StationMerchantDetail from './StationMerchantDetail';
import PageLoader from 'components/wholePageLoader/PageLoader';
import MapView from 'components/detailMapData/MapView';
import { ListOrderData } from 'reactRedux/thunks/orderThunk/orderThunk';
import ActivatedOrderListTable from './ActivatedOrderListTable';
import { FaEdit, FaFilePdf, FaArchive, FaTrashRestore } from 'react-icons/fa';
import IsDeactivateModal from 'components/popUpModel/IsDeactivateModal';
import Sidebar from 'components/sidebar/Sidebar';
import { sideBarRoutes } from 'utils/StaticData';
import BackButton from 'components/backButton/BackButton';
import { Controller, useForm } from 'react-hook-form';
import { YearOptionsForInvoice } from 'utils/StaticData';
import CommonReactSelect from 'components/reactSelectCommon/CommonReactSelect';
import { QuarterOptionsForInvoice } from 'utils/StaticData';
import {
  DownloadSettlementsPDF,
  SendSettlementsEmail,
} from 'reactRedux/thunks/settlementsThunk/SettlementsThunk';
import { yupResolver } from '@hookform/resolvers/yup';
import { generateInvoiceValidationSchema } from 'utils/CommonValidation';
import {
  selectDaysOptions,
  languageOptions,
  monthsSelectionsOptions,
} from 'utils/StaticData';
import { regionLanguageConfig } from 'utils/StaticData';
import { weekDays } from 'utils/StaticData';
import OpeningHoursTable from './OpeningHoursTable';
import { formatDate } from 'utils/CommonFunction';

const MerchantDetail = () => {
  const { merchantDetailId } = useParams();
  const [page, setPage] = useState(1);
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const currentYear = new Date().getFullYear();
  const { viewMerchantDetails, isViewLoading, isArchiveLoading } = useSelector(
    (state) => state.MerchantSlice,
  );
  const defaultYearOption = YearOptionsForInvoice.find(
    // eslint-disable-next-line eqeqeq
    (option) => option.label == currentYear,
  );

  const getDefaultLanguage = (regionId) => {
    const config = regionLanguageConfig.find((c) => c.regionId === regionId);
    const languageId = config ? config.languageId : 'en'; // Default to English
    return languageOptions.find((opt) => opt.id === languageId);
  };

  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    clearErrors,
    setValue,
    formState: { errors },
  } = useForm({
    mode: 'all',
    resolver: yupResolver(generateInvoiceValidationSchema),
    defaultValues: {
      year: defaultYearOption || null,
      daysSelect: {
        id: 1,
        label: 'Quarters',
      },
      quarter: null,
      month: null,
      language: getDefaultLanguage(viewMerchantDetails?.regionId?.regionId) || {
        id: 'en',
        label: 'English',
      },
    },
  });
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { adminDetails } = useSelector((state) => state.authSlice);
  const { isPDFDownload, isEmailSending } = useSelector(
    (state) => state.SettlementsSlice,
  );
  const { orderListData, isLoading } = useSelector((state) => state.OrderSlice);

  // Add new state to track success
  const [isSuccess, setIsSuccess] = useState(false);
  const [archiveModalOpen, setArchiveModalOpen] = useState(false);

  // Function to check if merchant is currently open
  const isMerchantOpen = () => {
    if (
      !viewMerchantDetails?.merchantHours ||
      !viewMerchantDetails?.merchantTemporaryHours
    ) {
      return false;
    }

    const now = new Date();
    const currentDay = now.getDay(); // 0 = Sunday, 1 = Monday, etc.
    const currentTime = now.toTimeString().substring(0, 5); // HH:MM format
    const currentDate = now.toISOString().split('T')[0]; // YYYY-MM-DD format

    // Check temporary closures first
    const activeClosure = viewMerchantDetails.merchantTemporaryHours.find(
      (closure) => {
        return (
          currentDate >= closure.startDate && currentDate <= closure.endDate
        );
      },
    );

    if (activeClosure) {
      return false; // Closed due to temporary closure
    }

    // Check regular hours
    const todayHours = viewMerchantDetails.merchantHours.filter(
      (hour) => hour.dayOfWeek === currentDay,
    );

    if (todayHours.length === 0) {
      return false; // No hours set for today
    }

    // Check if current time falls within any period
    return todayHours.some((hour) => {
      const openTime = hour.openTime.substring(0, 5);
      const closeTime = hour.closeTime.substring(0, 5);
      return currentTime >= openTime && currentTime <= closeTime;
    });
  };

  useEffect(() => {
    if (merchantDetailId) {
      dispatch(ViewMerchantData({ id: merchantDetailId }));
    }
  }, [merchantDetailId, dispatch]);

  const getOrderList = useCallback(() => {
    dispatch(
      ListOrderData({
        page: page,
        limit: 10,
        merchantId: merchantDetailId,
        rentalStatus: 1,
      }),
    );
  }, [dispatch, page, merchantDetailId]);

  useEffect(() => {
    getOrderList();
  }, [getOrderList]);

  const handleArchiveConfirm = () => {
    const isCurrentlyHidden = viewMerchantDetails?.isHidden;
    dispatch(
      ArchiveMerchant(
        merchantDetailId,
        { isHidden: !isCurrentlyHidden },
        () => {
          setArchiveModalOpen(false);
          dispatch(ViewMerchantData({ id: merchantDetailId }));
        },
      ),
    );
  };

  const handleGenerate = (type) => (e) => {
    const selectedDays = watch('daysSelect')?.id;
    const payload = {
      id: merchantDetailId,
      year: watch('year')?.label,
      ...(selectedDays === 1 && { quarter: watch('quarter')?.id }),
      ...(selectedDays === 2 && { month: watch('month')?.id }),
      language: watch('language')?.id,
    };

    if (type === 'email') {
      dispatch(SendSettlementsEmail(payload, () => setIsSuccess(true)));
    } else {
      dispatch(
        DownloadSettlementsPDF(
          { ...payload, name: viewMerchantDetails?.name },
          () => setIsSuccess(true),
        ),
      );
    }
  };

  // Update useEffect to close modal only on success
  useEffect(() => {
    if (isSuccess && !isPDFDownload && !isEmailSending) {
      onClose();
      reset();
      setIsSuccess(false);
    }
  }, [isPDFDownload, isEmailSending, isSuccess, onClose, reset]);

  useEffect(() => {
    if (isOpen) {
      setValue(
        'language',
        getDefaultLanguage(viewMerchantDetails?.regionId?.regionId),
      );
    }
  }, [isOpen, setValue, viewMerchantDetails]);

  const priceStrategyData = viewMerchantDetails?.priceStrategy || {};

  return (
    <>
      {isViewLoading ? (
        <PageLoader />
      ) : viewMerchantDetails ? (
        <>
          <Box pt={{ base: '60px', md: '80px', xl: '80px' }}>
            <Flex justifyContent="space-between" mb={4} gap="3">
              <BackButton
                fallbackUrl="/admin/merchants"
                label="Back to Merchants"
              />
              <Flex gap="3">
                {adminDetails?.role !== sideBarRoutes?.Merchant && (
                  <Button
                    py="10px"
                    px="15px"
                    fontWeight="semibold"
                    borderRadius="8px"
                    bg="primary"
                    color="white"
                    _hover={{ bg: 'primary', opacity: 0.8 }}
                    type="button"
                    leftIcon={<FaEdit size={22} />}
                    onClick={() =>
                      navigate(
                        `/admin/merchants/edit/${viewMerchantDetails?.merchantId}`,
                      )
                    }
                  >
                    Edit Merchant
                  </Button>
                )}
                {adminDetails?.role !== sideBarRoutes?.Merchant && (
                  <Button
                    py="10px"
                    px="15px"
                    fontWeight="semibold"
                    borderRadius="8px"
                    bg="primary"
                    color="white"
                    _hover={{ bg: 'primary', opacity: 0.8 }}
                    type="button"
                    leftIcon={<FaFilePdf size={25} />}
                    onClick={() => onOpen()}
                  >
                    Generate Invoice
                  </Button>
                )}
                {viewMerchantDetails?.inMaintenance && (
                  <Text
                    bg="red.200"
                    color="red.600"
                    px={4}
                    py={2}
                    borderRadius="md"
                    fontWeight="bold"
                    fontSize="sm"
                    boxShadow="sm"
                  >
                    Merchant is in maintenance
                  </Text>
                )}

                {viewMerchantDetails?.isTemporarilyClosed && (
                  <Text
                    bg="orange"
                    color="orange.600"
                    px={4}
                    py={2}
                    borderRadius="md"
                    fontWeight="bold"
                    fontSize="sm"
                    boxShadow="sm"
                  >
                    Merchant is temporarily closed
                  </Text>
                )}

                {viewMerchantDetails?.isComingSoon && (
                  <Text
                    bg="green.200"
                    color="green.700"
                    px={4}
                    py={2}
                    borderRadius="md"
                    fontWeight="bold"
                    fontSize="sm"
                    boxShadow="sm"
                  >
                    Coming Soon
                  </Text>
                )}

                <Text
                  bg={isMerchantOpen() ? 'green.200' : 'red.200'}
                  color={isMerchantOpen() ? 'green.600' : 'red.600'}
                  px={4}
                  py={2}
                  borderRadius="md"
                  fontWeight="bold"
                  fontSize="sm"
                  boxShadow="sm"
                >
                  Venue {isMerchantOpen() ? 'Online' : 'Offline'}
                </Text>
              </Flex>
            </Flex>
            <Flex direction="column">
              <Card
                w="100%"
                boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                borderRadius="20px"
                paddingInline="40px"
                paddingBlock="40px"
                overflowX={{ sm: 'scroll', lg: 'hidden' }}
              >
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Merchant Name :</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.name}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Location Id :</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.locationId}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Legal Name :</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.legalName || 'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Currency :</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.regionId?.currency}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Email:</Text>
                    <Flex flexDirection="column">
                      {viewMerchantDetails?.merchantEmail ? (
                        viewMerchantDetails.merchantEmail
                          .split(',')
                          .map((e) => e.trim())
                          .filter(Boolean)
                          .map((email, i) => (
                            <Text key={i} fontWeight="semibold">
                              {email}
                            </Text>
                          ))
                      ) : (
                        <Text fontWeight="semibold">—</Text>
                      )}
                    </Flex>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Commission:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.commission}%
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Timezone:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.timezone || 'N/A'}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">IBAN:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.bankAccountNumber}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">
                      {viewMerchantDetails?.regionId?.regionId === 2
                        ? 'NIF'
                        : 'KvK'}
                      :
                    </Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.NIF}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Include Powerbank Cost:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.includePowerbankCost ? 'Yes' : 'No'}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Include Customer Purchases:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.includeCustomerPurchases
                        ? 'Yes'
                        : 'No'}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Include Transaction Cost:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.includeTransactionCost
                        ? 'Yes'
                        : 'No'}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Include E-invoice Fee:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.includeEInvoiceFee ? 'Yes' : 'No'}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Free Usage Mode:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.freeUsageMode ? 'Yes' : 'No'}
                    </Text>
                  </Flex>

                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">VAT Number:</Text>
                    <Text fontWeight="semibold">
                      {viewMerchantDetails?.vatNumber || 'N/A'}
                    </Text>
                  </Flex>

                  <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Legal Address:</Text>
                      <Text fontWeight="semibold">
                        {(() => {
                          const {
                            legalAddressLine1,
                            legalAddressLine2,
                            legalZipCode,
                            legalCity,
                            legalProvince,
                            legalCountry,
                          } = viewMerchantDetails || {};

                          const addressParts = [
                            legalAddressLine1,
                            legalAddressLine2,
                            legalZipCode,
                            legalCity,
                            legalProvince,
                            legalCountry,
                          ].filter((part) => part && part.trim() !== '');

                          return addressParts.length > 0
                            ? addressParts.join(', ')
                            : 'N/A';
                        })()}
                      </Text>
                    </Flex>
                  </GridItem>

                  <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
                    <Flex
                      alignItems={{ sm: 'start', md: 'center' }}
                      direction={{ sm: 'column', md: 'row' }}
                      gap="10px"
                      padding="4px 8px"
                      borderLeft={'1px solid #ccc'}
                    >
                      <Text fontWeight="normal">Address :</Text>
                      <Text fontWeight="semibold">
                        {viewMerchantDetails?.address}
                      </Text>
                    </Flex>
                  </GridItem>

                  <GridItem colSpan={{ base: 1, md: 2, lg: 3 }}>
                    {/* <Text fontWeight="normal" mb={2}>
                    Coordinates :
                  </Text> */}
                    <MapView
                      coordinates={
                        viewMerchantDetails?.coordinates || [23.01052, 72.51129]
                      }
                      isChange={false}
                    />
                  </GridItem>
                </SimpleGrid>
              </Card>

              {/* Fiskaly Managed Organization (RAT-959) - served from our DB,
                  no live Fiskaly call. Spain (region 2) merchants only;
                  Fiskaly does not apply to other regions. */}
              {viewMerchantDetails?.regionId?.regionId === 2 && (
                <>
                  <Box pt="30px" alignItems="left">
                    <Text fontSize="2xl" fontWeight="medium">
                      Fiskaly Managed Organization
                    </Text>
                  </Box>
                  <Card
                    w="100%"
                    boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                    borderRadius="20px"
                    paddingInline="40px"
                    paddingBlock="40px"
                    overflowX={{ sm: 'scroll', lg: 'hidden' }}
                  >
                    <Flex alignItems="center" gap="12px" mb="20px">
                      <Text fontWeight="semibold">Status:</Text>
                      <Badge
                        colorScheme={
                          viewMerchantDetails?.fiskalyOrganization
                            ?.hasManagedOrg
                            ? 'green'
                            : 'gray'
                        }
                        borderRadius="full"
                        px="3"
                        py="1"
                      >
                        {viewMerchantDetails?.fiskalyOrganization?.hasManagedOrg
                          ? 'Created'
                          : 'Not created'}
                      </Badge>
                    </Flex>
                    {viewMerchantDetails?.fiskalyOrganization?.hasManagedOrg ? (
                      <SimpleGrid
                        columns={{ base: 1, md: 2, lg: 3 }}
                        spacing={4}
                      >
                        {[
                          [
                            'Fiskaly Org ID',
                            viewMerchantDetails?.fiskalyOrganization
                              ?.fiskalyOrganizationId,
                          ],
                          [
                            'Display Name',
                            viewMerchantDetails?.fiskalyOrganization
                              ?.displayName,
                          ],
                          [
                            'Legal Name',
                            viewMerchantDetails?.fiskalyOrganization?.name,
                          ],
                          [
                            'Taxpayer Status',
                            viewMerchantDetails?.fiskalyOrganization
                              ?.taxpayerSyncStatus,
                          ],
                          [
                            'Agreement',
                            viewMerchantDetails?.fiskalyOrganization
                              ?.agreementStatus,
                          ],
                          [
                            'Signer',
                            viewMerchantDetails?.fiskalyOrganization
                              ?.signerStatus,
                          ],
                          [
                            'Last Error',
                            viewMerchantDetails?.fiskalyOrganization?.lastError,
                          ],
                          [
                            'Created At',
                            viewMerchantDetails?.fiskalyOrganization?.createdAt
                              ? formatDate(
                                  viewMerchantDetails.fiskalyOrganization
                                    .createdAt,
                                )
                              : null,
                          ],
                          [
                            'Last Synced',
                            viewMerchantDetails?.fiskalyOrganization?.syncedAt
                              ? formatDate(
                                  viewMerchantDetails.fiskalyOrganization
                                    .syncedAt,
                                )
                              : null,
                          ],
                          [
                            'Last Updated',
                            viewMerchantDetails?.fiskalyOrganization?.updatedAt
                              ? formatDate(
                                  viewMerchantDetails.fiskalyOrganization
                                    .updatedAt,
                                )
                              : null,
                          ],
                        ].map(([label, value]) => (
                          <Flex
                            key={label}
                            alignItems={{ sm: 'start', md: 'center' }}
                            direction={{ sm: 'column', md: 'row' }}
                            gap="10px"
                            padding="4px 8px"
                            borderLeft={'1px solid #ccc'}
                          >
                            <Text fontWeight="normal">{label} :</Text>
                            <Text fontWeight="semibold">{value || 'N/A'}</Text>
                          </Flex>
                        ))}
                      </SimpleGrid>
                    ) : (
                      <Text color="gray.500">
                        This merchant does not have a Fiskaly managed
                        organization yet.
                      </Text>
                    )}
                    {viewMerchantDetails?.fiskalyOrganization?.hasManagedOrg &&
                      viewMerchantDetails?.fiskalyOrganization?.stationClients
                        ?.length > 0 && (
                        <Box mt="24px">
                          <Text fontWeight="semibold" mb="10px">
                            POS Clients
                          </Text>
                          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={3}>
                            {viewMerchantDetails.fiskalyOrganization.stationClients.map(
                              (client) => (
                                <Flex
                                  key={client.stationId}
                                  alignItems="center"
                                  gap="10px"
                                  padding="4px 8px"
                                  borderLeft="1px solid #ccc"
                                >
                                  <Text fontWeight="normal">
                                    {client.stationId} :
                                  </Text>
                                  <Badge
                                    colorScheme={
                                      client.syncStatus === 'SYNCED'
                                        ? 'green'
                                        : client.syncStatus === 'FAILED'
                                          ? 'red'
                                          : 'gray'
                                    }
                                    borderRadius="full"
                                    px="3"
                                    py="1"
                                  >
                                    {client.syncStatus || 'N/A'}
                                  </Badge>
                                </Flex>
                              ),
                            )}
                          </SimpleGrid>
                        </Box>
                      )}
                  </Card>
                </>
              )}

              <Box pt="30px" alignItems="left">
                <Text fontSize="2xl" fontWeight="medium">
                  Price Strategy
                </Text>
              </Box>
              <Card
                w="100%"
                boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                borderRadius="20px"
                paddingInline="40px"
                paddingBlock="40px"
                overflowX={{ sm: 'scroll', lg: 'hidden' }}
                mt="20px"
              >
                <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">ID :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.id ?? 'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Name :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.name || 'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Region ID :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.regionId ?? 'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Baije Price ID :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.baijePriceId || 'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Price Per Time Interval :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.price ??
                        viewMerchantDetails?.price ??
                        'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Time Interval :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.priceMinute ??
                        viewMerchantDetails?.priceMinute ??
                        'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Free Duration :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.freeDuration ??
                        viewMerchantDetails?.freeDuration ??
                        'N/A'}{' '}
                      Min
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Deposit :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.deposit ??
                        viewMerchantDetails?.deposit ??
                        'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Overtime Fees :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.overtimeFees ??
                        viewMerchantDetails?.overtimeFees ??
                        'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Days Limit :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.daysLimit ??
                        viewMerchantDetails?.daysLimit ??
                        'N/A'}
                    </Text>
                  </Flex>
                  <Flex
                    alignItems={{ sm: 'start', md: 'center' }}
                    direction={{ sm: 'column', md: 'row' }}
                    gap="10px"
                    padding="4px 8px"
                    borderLeft={'1px solid #ccc'}
                  >
                    <Text fontWeight="normal">Day Capped Amount :</Text>
                    <Text fontWeight="semibold">
                      {priceStrategyData?.maxPrice ??
                        viewMerchantDetails?.maxPrice ??
                        'N/A'}
                    </Text>
                  </Flex>
                </SimpleGrid>
              </Card>

              {/* hours section */}
              {viewMerchantDetails?.merchantHours?.length > 0 && (
                <>
                  <Box pt="30px" alignItems="left">
                    <Text fontSize="2xl" fontWeight="medium">
                      Opening Hours
                    </Text>
                  </Box>
                  <Card
                    w="100%"
                    boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                    borderRadius="20px"
                    paddingInline="40px"
                    paddingBlock="40px"
                    overflowX={{ sm: 'scroll', lg: 'hidden' }}
                    mt="20px"
                  >
                    <OpeningHoursTable
                      tableOpeningHoursData={viewMerchantDetails?.merchantHours}
                      isLoading={isViewLoading}
                    />
                  </Card>
                </>
              )}

              {/* Temporary Closures section */}
              {viewMerchantDetails?.merchantTemporaryHours?.length > 0 && (
                <>
                  <Box pt="30px" alignItems="left">
                    <Text fontSize="2xl" fontWeight="medium">
                      HOLIDAY HOURS
                    </Text>
                  </Box>
                  <Card
                    w="100%"
                    boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                    borderRadius="20px"
                    paddingInline="40px"
                    paddingBlock="40px"
                    overflowX={{ sm: 'scroll', lg: 'hidden' }}
                    mt="20px"
                  >
                    <OpeningHoursTable
                      tableOpeningHoursData={
                        viewMerchantDetails?.merchantTemporaryHours
                      }
                      isLoading={isViewLoading}
                    />
                    {/* <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={4}>
                      {viewMerchantDetails.merchantTemporaryHours.map(
                        (closure, index) => (
                          <Card
                            key={index}
                            p={4}
                            borderRadius="lg"
                            boxShadow="sm"
                            border="1px solid"
                            borderColor="gray.200"
                          >
                            <VStack align="start" spacing={2}>
                              <Text fontSize="md" fontWeight="semibold">
                                {formatDate(closure.date)}
                              </Text>
                              <Text fontSize="sm" color="gray.600">
                                {closure.remark}
                              </Text>
                            </VStack>
                          </Card>
                        ),
                      )}
                    </SimpleGrid> */}
                  </Card>
                </>
              )}

              {viewMerchantDetails?.stations?.length > 0 && (
                <>
                  <Box pt="30px" alignItems="left">
                    <Text fontSize="2xl" fontWeight="medium">
                      Allocated Stations
                    </Text>
                  </Box>
                  <Card
                    w="100%"
                    boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                    borderRadius="20px"
                    paddingInline="40px"
                    paddingBlock="40px"
                    overflowX={{ sm: 'scroll', lg: 'hidden' }}
                    mt="20px"
                  >
                    <StationMerchantDetail
                      tableData={viewMerchantDetails?.stations}
                      isLoading={isViewLoading}
                    />
                  </Card>
                </>
              )}

              <>
                <Box pt="30px">
                  <Flex justify="space-between" align="center">
                    <Text fontSize="2xl" fontWeight="medium">
                      Activated Orders
                    </Text>
                    <Button
                      onClick={() =>
                        navigate('/admin/orders', {
                          state: {
                            merchant: viewMerchantDetails.name,
                            merchantId: viewMerchantDetails.merchantId,
                          },
                        })
                      }
                    >
                      View All
                    </Button>
                  </Flex>
                </Box>
                <Card
                  w="100%"
                  boxShadow="14px 17px 40px 4px rgba(112, 144, 176, 0.08)"
                  borderRadius="20px"
                  paddingInline="40px"
                  paddingBlock="40px"
                  overflowX={{ sm: 'scroll', lg: 'hidden' }}
                  mt="20px"
                >
                  <ActivatedOrderListTable
                    tableData={orderListData?.list}
                    isLoading={isLoading}
                  />
                </Card>
              </>

              {(adminDetails?.role === sideBarRoutes?.Admin ||
                adminDetails?.role === sideBarRoutes?.Dealer) && (
                <Flex
                  mt={6}
                  align="center"
                  justify="space-between"
                  bg={viewMerchantDetails?.isHidden ? 'green.50' : 'red.50'}
                  border="1px solid"
                  borderColor={
                    viewMerchantDetails?.isHidden ? 'green.200' : 'red.200'
                  }
                  borderRadius="10px"
                  px={4}
                  py={3}
                >
                  <Flex align="center" gap={2}>
                    <Icon
                      as={
                        viewMerchantDetails?.isHidden
                          ? FaTrashRestore
                          : FaArchive
                      }
                      color={
                        viewMerchantDetails?.isHidden ? 'green.600' : 'red.500'
                      }
                      w="14px"
                      h="14px"
                    />
                    <Text
                      fontSize="sm"
                      fontWeight="700"
                      color={
                        viewMerchantDetails?.isHidden ? 'green.700' : 'red.600'
                      }
                    >
                      {viewMerchantDetails?.isHidden
                        ? 'This merchant is currently archived'
                        : 'Archive this merchant to hide it from active lists'}
                    </Text>
                  </Flex>
                  <Button
                    size="sm"
                    colorScheme={
                      viewMerchantDetails?.isHidden ? 'green' : 'red'
                    }
                    borderRadius="8px"
                    leftIcon={
                      <Icon
                        as={
                          viewMerchantDetails?.isHidden
                            ? FaTrashRestore
                            : FaArchive
                        }
                        w="12px"
                        h="12px"
                      />
                    }
                    isLoading={isArchiveLoading}
                    onClick={() => setArchiveModalOpen(true)}
                  >
                    {viewMerchantDetails?.isHidden
                      ? 'Restore Merchant'
                      : 'Archive Merchant'}
                  </Button>
                </Flex>
              )}
            </Flex>

            {archiveModalOpen && (
              <IsDeactivateModal
                isOpen={archiveModalOpen}
                onClose={() => setArchiveModalOpen(false)}
                title={
                  viewMerchantDetails?.isHidden
                    ? 'Restore Merchant'
                    : 'Archive Merchant'
                }
                description={
                  viewMerchantDetails?.isHidden
                    ? `Are you sure you want to restore ${viewMerchantDetails?.name}?`
                    : `Are you sure you want to archive ${viewMerchantDetails?.name}?`
                }
                confirmText={
                  viewMerchantDetails?.isHidden ? 'Restore' : 'Archive'
                }
                confirmColorScheme={
                  viewMerchantDetails?.isHidden ? 'green' : 'red'
                }
                onConfirm={handleArchiveConfirm}
                isLoading={isArchiveLoading}
                isActivating={!!viewMerchantDetails?.isHidden}
              />
            )}

            {/* Modal for generating invoice */}
            <Modal
              isOpen={isOpen}
              onClose={onClose}
              isCentered
              closeOnOverlayClick={false}
              size={{ base: 'sm', md: 'md', lg: 'lg' }}
            >
              <ModalOverlay />
              <ModalContent>
                <form
                  onSubmit={handleSubmit(handleGenerate('download'))}
                  noValidate
                >
                  <ModalHeader>Generate Invoice</ModalHeader>
                  <ModalCloseButton
                    onClick={() => {
                      reset();
                      clearErrors();
                    }}
                  />
                  <ModalBody>
                    <VStack spacing={4} align="stretch">
                      {/* Warning message for Spain region */}
                      {viewMerchantDetails?.regionId?.regionId === 2 && (
                        <Alert status="warning" borderRadius="md">
                          <AlertIcon />
                          <Text fontSize="sm">
                            Legal/compliant invoice requires a valid Tax ID
                            (NIF) and full address with postal code.
                          </Text>
                        </Alert>
                      )}
                      <Controller
                        name="year"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CommonReactSelect
                            name="year"
                            type="select"
                            options={YearOptionsForInvoice}
                            value={value ? [value] : []}
                            onChange={onChange}
                            register={register}
                            error={errors?.year}
                            getOptionLabel={(e) => e?.label}
                            getOptionValue={(e) => e?.id}
                            placeholderValue="Year"
                          />
                        )}
                      />

                      <Controller
                        name="daysSelect"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CommonReactSelect
                            name="daysSelect"
                            type="select"
                            options={selectDaysOptions}
                            value={value ? [value] : []}
                            // onChange={onChange}
                            onChange={(selected) => {
                              onChange(selected);
                              if (selected?.id === 2) {
                                setValue('quarter', null);
                                clearErrors('quarter');
                              } else if (selected?.id === 1) {
                                setValue('month', null);
                                clearErrors('month');
                              }
                            }}
                            register={register}
                            error={errors?.year}
                            getOptionLabel={(e) => e?.label}
                            getOptionValue={(e) => e?.id}
                            placeholderValue="Days"
                          />
                        )}
                      />

                      {watch('daysSelect')?.id === 1 ? (
                        <>
                          <Controller
                            name="quarter"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <CommonReactSelect
                                label="Quarter"
                                name="quarter"
                                type="select"
                                options={QuarterOptionsForInvoice}
                                value={value ? [value] : []}
                                onChange={onChange}
                                register={register}
                                isRequired={true}
                                error={errors?.quarter}
                                getOptionLabel={(e) => e?.label}
                                getOptionValue={(e) => e?.id}
                              />
                            )}
                          />
                        </>
                      ) : (
                        <>
                          <Controller
                            name="month"
                            control={control}
                            render={({ field: { onChange, value } }) => (
                              <CommonReactSelect
                                label="Months"
                                name="month"
                                type="select"
                                options={monthsSelectionsOptions}
                                value={value ? [value] : []}
                                onChange={onChange}
                                register={register}
                                isRequired={true}
                                error={errors?.month}
                                getOptionLabel={(e) => e?.label}
                                getOptionValue={(e) => e?.id}
                              />
                            )}
                          />
                        </>
                      )}

                      <Controller
                        name="language"
                        control={control}
                        render={({ field: { onChange, value } }) => (
                          <CommonReactSelect
                            label="Language"
                            name="language"
                            type="select"
                            options={languageOptions}
                            value={value ? [value] : []}
                            onChange={onChange}
                            register={register}
                            error={errors?.year}
                            getOptionLabel={(e) => e?.label}
                            getOptionValue={(e) => e?.id}
                            placeholderValue="Language"
                          />
                        )}
                      />
                    </VStack>
                  </ModalBody>
                  <ModalFooter>
                    <Button
                      type="button"
                      bg="primary"
                      color="white"
                      _hover={{ bg: 'primary', opacity: 0.8 }}
                      mr={3}
                      disabled={isEmailSending || isPDFDownload}
                      isLoading={isEmailSending}
                      loadingText="Sending..."
                      fontSize="sm"
                      fontWeight="500"
                      onClick={handleSubmit(handleGenerate('email'))}
                      isDisabled={!viewMerchantDetails?.merchantEmail}
                    >
                      Send Mail
                    </Button>
                    <Button
                      type="button"
                      bg="primary"
                      color="white"
                      _hover={{ bg: 'primary', opacity: 0.8 }}
                      mr={3}
                      disabled={isEmailSending || isPDFDownload}
                      isLoading={isPDFDownload}
                      loadingText="Generating..."
                      fontSize="sm"
                      fontWeight="500"
                      onClick={handleSubmit(handleGenerate('download'))}
                    >
                      Download Invoice
                    </Button>
                  </ModalFooter>
                </form>
              </ModalContent>
            </Modal>
          </Box>
        </>
      ) : (
        <Text>No merchant details found.</Text>
      )}
    </>
  );
};

export default MerchantDetail;
